"use client"

import React, { useMemo, useState, useTransition } from "react"
import { useRouter } from "next/navigation"
import { createBusinessUserAction } from "@/actions/accountUserActions"
import type { UserRoleRO } from "@ub/requisition-approval-client"
import { CheckCircleIcon } from "lucide-react"
import { toast } from "sonner"
import { z } from "zod"

import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { cn } from "@/lib/utils"
import { RadioGroup, RadioGroupItem } from "@/components/ui/components"
import { Button } from "@/components/ui/components/button"
import { Checkbox } from "@/components/ui/components/checkbox"
import { Input } from "@/components/ui/components/input"
import { Label } from "@/components/ui/components/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/components/select"
import { aggregatePermissionsForRolesLocal } from "@/components/account/users/users-helper"
import type { UbAccountUserNewViewStoryblok } from "@/components/storyblok/component-types"

import { FormRow } from "../shared/FormRow"

const newUserSchema = z.object({
  // Use a free-text string like in AccountUserEditProfileForm – backend stores the localized title
  salutation: z.string().min(1, "Anrede ist erforderlich"),
  firstName: z.string().min(1, "Vorname ist erforderlich"),
  lastName: z.string().min(1, "Nachname ist erforderlich"),
  email: z.string().email("Ungültige E-Mail-Adresse"),
  department: z.string().max(200).optional().or(z.literal("")),
  active: z.boolean().optional(),
  phone: z.string().max(50).optional().or(z.literal("")),
  fax: z.string().max(50).optional().or(z.literal("")),
  language: z.string().optional(),
  roleIds: z.array(z.string()).default([]),
  spendLimit: z.string().optional(),
  budget: z.string().optional(),
  budgetCycle: z.enum(["weekly", "monthly", "quarterly", "yearly"]).optional(),
})

export type NewUserFormValues = z.infer<typeof newUserSchema>

export function AccountUserNewForm({
  customerNo,
  allRoles,
  preferredLanguages,
  blok,
  sbLocale,
}: {
  customerNo: string
  allRoles: UserRoleRO[]
  preferredLanguages: string[]
  blok: UbAccountUserNewViewStoryblok
  sbLocale: string
}) {
  const initiallyFixedRoleIds = useMemo(
    () =>
      allRoles
        .filter((r) => r.fixed)
        .map((r) => r.roleID ?? "")
        .filter(Boolean) as string[],
    [allRoles]
  )
  const [values, setValues] = useState<NewUserFormValues>({
    salutation: "",
    firstName: "",
    lastName: "",
    email: "",
    department: "",
    active: true,
    phone: "",
    fax: "",
    language: preferredLanguages?.[0] ?? "de_DE",
    roleIds: initiallyFixedRoleIds,
    spendLimit: "",
    budget: "",
    budgetCycle: "monthly",
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [successMsg, setSuccessMsg] = useState<string>("")
  const [isPending, startTransition] = useTransition()

  const permissions = useMemo(
    () => aggregatePermissionsForRolesLocal(allRoles, values.roleIds),
    [allRoles, values.roleIds]
  )

  const router = useRouter()

  function setField<K extends keyof NewUserFormValues>(key: K, val: NewUserFormValues[K]) {
    setValues((v) => ({ ...v, [key]: val }))
  }

  function toggleRole(id: string, fixed?: boolean) {
    if (fixed) return
    setValues((v) => ({
      ...v,
      roleIds: v.roleIds.includes(id) ? v.roleIds.filter((x) => x !== id) : [...v.roleIds, id],
    }))
  }

  async function onCreate() {
    setSuccessMsg("")
    setErrors({})
    const parsed = newUserSchema.safeParse(values)
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of parsed.error.issues) {
        const path = issue.path.join(".")
        fieldErrors[path] = issue.message
      }
      setErrors(fieldErrors)
      return
    }

    startTransition(async () => {
      const res = await createBusinessUserAction(customerNo, {
        salutation: values.salutation,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        department: values.department,
        active: !!values.active,
        phone: values.phone,
        fax: values.fax,
        language: values.language,
        roleIds: values.roleIds,
        spendLimit: values.spendLimit,
        budget: values.budget,
        budgetCycle: values.budgetCycle,
      } as any)
      if ((res as any)?.error) {
        setErrors({ form: blok.label_form_error ?? "" })
      } else {
        toast.success(blok.label_form_success ?? "", {
          icon: <CheckCircleIcon className={"size-4 text-green-500"} />,
          description: "",
        })
      }
    })
  }

  return (
    <div className="flex flex-col gap-2 lg:gap-6">
      {Object.values(errors).map((value) => (
        <p key={value} className="text-sm text-red-600" />
      ))}
      {successMsg && <p className="text-sm text-green-700">{successMsg}</p>}

      <FormRow label={blok.label_salutation} htmlFor="salutation" required>
        <RadioGroup
          value={values.salutation}
          onValueChange={(v) => setField("salutation", v as any)}
          name="salutation"
          className={cn("flex flex-row justify-between gap-1 rounded-lg border  border-gray-300 bg-white px-5 py-3")}
        >
          <div className="flex w-fit items-center space-x-2 text-base outline-none ring-0 hover:border-black">
            <RadioGroupItem value={blok.label_salutation_female ?? ""} id="r1" />
            <Label className={"font-bold"} htmlFor="r1">
              {blok.label_salutation_female}
            </Label>
          </div>
          <div className="flex w-fit items-center space-x-2 text-base outline-none ring-0 hover:border-black">
            <RadioGroupItem value={blok.label_salutation_male ?? ""} id="r2" />
            <Label className={"font-bold"} htmlFor="r2">
              {blok.label_salutation_male}
            </Label>
          </div>
          <div className="flex w-fit items-center space-x-2 text-base outline-none ring-0 hover:border-black">
            <RadioGroupItem value={blok.label_salutation_divers ?? ""} id="r3" />
            <Label className={"font-bold"} htmlFor="r3">
              {blok.label_salutation_divers}
            </Label>
          </div>
        </RadioGroup>
        {errors.salutation && <p className="mt-1 text-xs text-red-600">{errors.salutation}</p>}
      </FormRow>

      <FormRow label={blok.label_firstname} htmlFor="firstName" required>
        <Input id="firstName" value={values.firstName} onChange={(e) => setField("firstName", e.target.value)} />
        {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>}
      </FormRow>

      <FormRow label={blok.label_lastname} htmlFor="lastName" required>
        <Input id="lastName" value={values.lastName} onChange={(e) => setField("lastName", e.target.value)} />
        {errors.lastName && <p className="mt-1 text-xs text-red-600">{errors.lastName}</p>}
      </FormRow>

      <FormRow label={blok.label_email} htmlFor="email" required>
        <Input id="email" type="email" value={values.email} onChange={(e) => setField("email", e.target.value)} />
        {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
      </FormRow>

      <FormRow label={blok.label_department} htmlFor="department">
        <Input
          id="department"
          value={values.department ?? ""}
          onChange={(e) => setField("department", e.target.value)}
        />
      </FormRow>

      <div className="grid grid-cols-1 items-start gap-2 md:grid-cols-3 md:gap-8">
        <div />
        <div className="flex items-center space-x-2 md:col-span-2">
          <Checkbox id="active" checked={!!values.active} onCheckedChange={(v) => setField("active", !!v)} />
          <Label htmlFor="active">{blok.label_active}</Label>
        </div>
      </div>

      <FormRow label={blok.label_phone} htmlFor="phone">
        <Input id="phone" type="tel" value={values.phone ?? ""} onChange={(e) => setField("phone", e.target.value)} />
      </FormRow>

      <FormRow label={blok.label_fax} htmlFor="fax">
        <Input id="fax" value={values.fax ?? ""} onChange={(e) => setField("fax", e.target.value)} />
      </FormRow>

      <FormRow label={blok.label_preferred_language} htmlFor="language" required>
        <Select value={values.language} onValueChange={(v) => setField("language", v as any)}>
          <SelectTrigger id="language">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {preferredLanguages.map((language) => (
              <SelectItem key={language} value={language}>
                {blok["label_language_" + language]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormRow>

      <FormRow label={blok.label_roles} htmlFor="roles">
        <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-300 bg-white p-4 hover:border-black sm:grid-cols-2">
          {allRoles.map((role) => (
            <div key={role.roleID} className={cn("flex items-center space-x-2")}>
              <Checkbox
                id={role.roleID}
                checked={values.roleIds.includes(role.roleID ?? "")}
                onCheckedChange={() => role.roleID && toggleRole(role.roleID, role.fixed)}
                disabled={role.fixed}
              />
              <Label htmlFor={role.roleID!} className="font-normal hover:cursor-pointer">
                {role.roleDisplayName ?? role.roleID}
              </Label>
            </div>
          ))}
        </div>
      </FormRow>

      <FormRow label={blok.label_permissions} htmlFor="permissions">
        <div className="space-y-1 pt-2 text-sm text-gray-700">
          {permissions.map((p) => (
            <p key={p.permissionID ?? p.permissionDisplayName}>{p.permissionDisplayName ?? p.permissionID}</p>
          ))}
          {permissions.length == 0 && <span className={"font-light"}>{blok.label_select_a_role}</span>}
        </div>
      </FormRow>

      <FormRow label={blok.label_limit_per_order} htmlFor="spend-limit">
        <div className="relative">
          <Input
            id="spend-limit"
            type="number"
            className="pl-12"
            value={values.spendLimit ?? ""}
            onChange={(e) => setField("spendLimit", e.target.value)}
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">EUR</span>
        </div>
      </FormRow>

      <FormRow label={blok.label_budget} htmlFor="budget">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              id="budget"
              type="number"
              className="pl-12"
              value={values.budget ?? ""}
              onChange={(e) => setField("budget", e.target.value)}
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">EUR</span>
          </div>
          <Select value={values.budgetCycle} onValueChange={(v) => setField("budgetCycle", v as any)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">{blok.label_weekly}</SelectItem>
              <SelectItem value="monthly">{blok.label_monthly}</SelectItem>
              <SelectItem value="quarterly">{blok.label_quarterly}</SelectItem>
              <SelectItem value="yearly">{blok.label_yearly}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </FormRow>

      <div className="flex justify-end gap-4 pt-8">
        <Button
          type="reset"
          variant="outline"
          onClick={() => {
            router.replace(getLinkWithPrefixFromURLAndLocale(`/account/users`, sbLocale))
          }}
        >
          {blok.label_cancel}
        </Button>
        <Button type="button" onClick={onCreate} disabled={isPending}>
          {isPending ? blok.label_creating : blok.label_create_user}
        </Button>
      </div>
    </div>
  )
}
